import Stack from "../Stack/Stack";
import { useAppContext } from '@/context/AppContext';
import { useEffect, useState, useCallback } from 'react';
import { MeasureTypes } from '@/types/core/Clock';

import './Logo.css';

export default function Logo() {
    const { clock } = useAppContext();
    const [activePart, setActivePart] = useState(0);

    const getMeasureBeats = useCallback((measure: MeasureTypes): number => {
        switch (measure) {
            case '3/4': return 3;
            case '6/8': return 6;
            case '7/8': return 7;
            case '9/8': return 9;
            case '12/8': return 12;
            default: return 4; // 4/4 time
        }
    }, []);

    useEffect(() => {
        if (!clock) return;

        let animationFrameId: number;

        const animate = () => {
            if (!clock) return;

            const currentTime = clock.getElapsedTime();
            const bps = clock.getBpm() / 60;
            const beatDuration = 1 / bps;
            const totalBeats = getMeasureBeats(clock.measure);
            
            // Calculate current beat based on elapsed time and BPM
            const beatCount = Math.floor(currentTime / beatDuration);
            const newBeat = (beatCount % totalBeats);
            
            if (newBeat !== activePart) {
                setActivePart(newBeat);
            }

            animationFrameId = requestAnimationFrame(animate);
        };

        animate();

        // Cleanup animation frame on unmount or when clock changes
        return () => {
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
        };
    }, [clock, activePart, getMeasureBeats]);

    return (
        <Stack direction="horizontal" align="center" gap="sm" className="logo">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" width={26} className="logo__icon">
                <g fill="#000" fill-rule="nonzero">
                    <path d="M31 1h77.58c8.571 0 15.52 6.949 15.52 15.52v93.09c0 8.571-6.949 15.52-15.52 15.52H15.52C6.949 125.13 0 118.181 0 109.61V32C0 14.88 13.88 1 31 1Z" className={`logo__icon__part ${activePart === 0 ? 'logo__icon__part--active' : ''}`} />
                    <path d="M240.49 125.12H147.4c-8.571 0-15.52-6.949-15.52-15.52V16.51c0-8.571 6.949-15.52 15.52-15.52h77.57c17.12 0 31 13.88 31 31v77.61c0 8.556-6.924 15.498-15.48 15.52Z" className={`logo__icon__part ${activePart === 1 ? 'logo__icon__part--active' : ''}`} />
                    <path d="M15.52 132.88h93.09c8.571 0 15.52 6.949 15.52 15.52v93.09c-.006 8.568-6.952 15.51-15.52 15.51H31c-17.12 0-31-13.88-31-31v-77.61c.006-8.568 6.952-15.51 15.52-15.51Z" className={`logo__icon__part ${activePart === 3 ? 'logo__icon__part--active' : ''}`} />
                    <path d="M224.97 257h-77.58c-8.564-.006-15.504-6.946-15.51-15.51V148.4c0-8.571 6.949-15.52 15.52-15.52h93.09c8.571 0 15.52 6.949 15.52 15.52v77.57A31 31 0 0 1 224.97 257Z" className={`logo__icon__part ${activePart === 2 ? 'logo__icon__part--active' : ''}`} />
                </g>
            </svg>
            <svg xmlns="http://www.w3.org/2000/svg" width={96} viewBox="0 0 1018.06 256" className="logo__text">
                <g data-name="Layer 2">
                    <path
                        d="M0 244.82V11.18A11.21 11.21 0 0 1 11.18 0h120.66q36.67 0 56.06 6.29a59.64 59.64 0 0 1 31.25 23.31Q231 46.63 231 71.07q0 21.32-9.09 36.76a71 71 0 0 1-25 25.06Q186.82 139 169.18 143q14.13 4.72 20.59 9.44 4.36 3.14 12.65 13.46t11.08 15.9l29.89 57.88a11.18 11.18 0 0 1-9.93 16.3h-64.37a11.17 11.17 0 0 1-9.83-5.86l-39.12-72.37q-8-15.2-14.32-19.73a33.16 33.16 0 0 0-19.38-5.94h-7v92.72A11.17 11.17 0 0 1 68.28 256h-57.1A11.17 11.17 0 0 1 0 244.82Zm79.45-141.09h33.36q5.4 0 20.95-3.5a20.88 20.88 0 0 0 12.84-8 23.61 23.61 0 0 0 5-14.84q0-12.4-7.85-19t-29.55-6.7H79.45ZM435.74 213.74h-89.81L335.82 248a11.19 11.19 0 0 1-10.72 8h-56.28a11.18 11.18 0 0 1-10.46-15.11L346.18 7.24A11.18 11.18 0 0 1 356.64 0h70.81a11.19 11.19 0 0 1 10.46 7.24l87.8 233.65A11.18 11.18 0 0 1 515.24 256h-58.4a11.18 11.18 0 0 1-10.7-7.93Zm-16.41-55.36-28.26-92-28 92ZM520.83 0h58.31a11.93 11.93 0 0 1 10.67 7.84L645 184.23 699.43 7.88A11.9 11.9 0 0 1 710.1 0h56c8.84 0 13.56 6.78 10.48 15.07l-86.79 233.64a12 12 0 0 1-10.47 7.29h-70.25a12 12 0 0 1-10.45-7.23L510.38 15.12C507.24 6.81 511.94 0 520.83 0ZM813.4 0h189.65a11.17 11.17 0 0 1 11.17 11.18v32.3a11.18 11.18 0 0 1-11.17 11.18H881.51v44.88h111.93a11.18 11.18 0 0 1 11.18 11.17v29.86a11.18 11.18 0 0 1-11.18 11.18H881.51V198h125.38a11.18 11.18 0 0 1 11.17 11.18v35.62a11.17 11.17 0 0 1-11.17 11.2H813.4a11.17 11.17 0 0 1-11.17-11.18V11.18A11.17 11.17 0 0 1 813.4 0Z"
                        style={{ fill: 'currentColor' }}
                    />
                </g>
            </svg>
        </Stack>
    );
}